<template>
  <Teleport to="body">
    <div
      class="home-rsd-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="t('title')"
      @click.self="onClose"
    >
      <div class="home-rsd-modal__card">
        <header class="home-rsd-modal__head">
          <h2 class="home-rsd-modal__title">{{ t('title') }}</h2>
          <button
            type="button"
            class="home-rsd-modal__close"
            :aria-label="t('close')"
            :title="t('close')"
            @click="onClose"
          >
            <i
              class="bi bi-x-lg"
              aria-hidden="true"
            />
          </button>
        </header>

        <div class="home-rsd-modal__body">
          <RsdItem
            v-if="id"
            ref="rsdRef"
            :id="id"
            embedded
            @saved="onSaved"
            @editable="onEditable"
          />
        </div>

        <footer class="home-rsd-modal__foot">
          <div class="home-rsd-modal__foot-left">
            <div
              class="home-rsd-modal__form-info"
              :title="t('formNumberHint')"
            >
              <i
                class="bi bi-info-circle"
                aria-hidden="true"
              />
              <span>{{ formNumberLabel }}</span>
            </div>
            <History
              v-if="id"
              model="Rsd"
              :id="Number(id)"
            />
          </div>
          <div class="home-rsd-modal__actions">
            <p
              v-if="saveError"
              class="home-rsd-modal__save-error"
              role="alert"
            >
              {{ saveError }}
            </p>
            <button
              type="button"
              class="home-rsd-modal__btn home-rsd-modal__btn--secondary"
              :disabled="exportBusy || !id"
              @click="onDownload"
            >
              <i
                class="bi bi-download"
                aria-hidden="true"
              />
              {{ t('download') }}
            </button>
            <button
              v-if="canEdit && !isEditing"
              type="button"
              class="home-rsd-modal__btn home-rsd-modal__btn--primary"
              @click="onEdit"
            >
              {{ t('edit') }}
            </button>
            <template v-else-if="canEdit && isEditing">
              <button
                type="button"
                class="home-rsd-modal__btn home-rsd-modal__btn--secondary"
                :disabled="saving"
                @click="onCancelEdit"
              >
                {{ t('cancel') }}
              </button>
              <button
                type="button"
                class="home-rsd-modal__btn home-rsd-modal__btn--primary"
                :disabled="!canSave"
                @click="onSave"
              >
                <i
                  class="bi bi-check2"
                  aria-hidden="true"
                />
                {{ saving ? t('saving') : t('save') }}
              </button>
            </template>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import History from '@/components/history/history.vue'
import RsdItem from '@/views/rsd/item.vue'
import { confirmExitEdit } from '@/composables/ui'
import { getRsdFormNumber } from '@/lib/sopsCatalog'
import { lockPageScroll, unlockPageScroll } from '@/lib/pageScrollLock'
import { useMessageStack } from '@/stores/messagestack'
import { useUserStore } from '@/stores/user'

const props = defineProps({
  id: { type: Number, required: true },
})

const emit = defineEmits(['close'])

const { t } = useI18n()
const { push } = useMessageStack()
const userStore = useUserStore()
const rsdRef = ref(null)
const editing = ref(false)
const saving = ref(false)
const saveError = ref('')
const fallbackFormNumber = getRsdFormNumber()

const formNumberLabel = computed(
  () => rsdRef.value?.formNumber || fallbackFormNumber
)
const exportBusy = computed(() => Boolean(rsdRef.value?.exportBusy))
const isEditing = computed(
  () => editing.value || Boolean(rsdRef.value?.isEditing)
)
const canEdit = computed(
  () => userStore.can(['rsd_edit', 'sa']) && !rsdRef.value?.isDeleted
)
/** Save доступен при edit (локальный флаг или Form lock). */
const canSave = computed(() => canEdit.value && isEditing.value && !saving.value)

function onEditable(value) {
  editing.value = Boolean(value)
}

async function onEdit() {
  saveError.value = ''
  const ok = await rsdRef.value?.startEdit?.()
  editing.value = Boolean(ok ?? rsdRef.value?.isEditing)
}

async function onCancelEdit() {
  if (!isEditing.value) {
    return
  }
  if (!(await confirmExitEdit())) {
    return
  }
  try {
    await rsdRef.value?.exitEdit?.()
  } catch {
    /* ignore */
  }
  editing.value = false
  saveError.value = ''
}

async function onClose() {
  if (isEditing.value) {
    if (!(await confirmExitEdit())) {
      return
    }
    try {
      await rsdRef.value?.exitEdit?.()
    } catch {
      /* ignore */
    }
  }
  editing.value = false
  saveError.value = ''
  emit('close')
}

async function onSave() {
  if (!canEdit.value || saving.value) {
    return
  }
  saveError.value = ''
  if (!isEditing.value) {
    await onEdit()
  }
  if (!isEditing.value) {
    saveError.value = t('saveNeedEdit')
    push({ type: 'error', message: saveError.value })
    return
  }
  saving.value = true
  try {
    await nextTick()
    const saveFn = rsdRef.value?.saveFromModal
    if (typeof saveFn !== 'function') {
      saveError.value = t('saveUnavailable')
      push({ type: 'error', message: saveError.value })
      return
    }
    const ok = await saveFn.call(rsdRef.value)
    if (ok === true) {
      editing.value = false
      saveError.value = ''
    } else {
      saveError.value = t('saveFailed')
    }
  } catch (err) {
    saveError.value = err?.message || t('saveFailed')
    push({ type: 'error', message: saveError.value })
  } finally {
    saving.value = false
  }
}

async function onDownload() {
  await rsdRef.value?.exportPdf?.()
}

function onSaved() {
  editing.value = false
  saveError.value = ''
}

onMounted(() => {
  document.body.classList.add('rsd-modal-open')
  lockPageScroll()
})

onUnmounted(() => {
  document.body.classList.remove('rsd-modal-open')
  unlockPageScroll()
})
</script>

<i18n>
{
  "ru": {
    "title": "Регуляторная стратегия лекарственного препарата",
    "close": "Закрыть",
    "edit": "Редактировать",
    "cancel": "Отмена",
    "formNumberHint": "Номер Forms (раздел Forms / Local SOPs)",
    "download": "Скачать",
    "save": "Сохранить",
    "saving": "Сохранение…",
    "saveFailed": "Не удалось сохранить",
    "saveNeedEdit": "Сначала включите редактирование",
    "saveUnavailable": "Форма ещё не готова к сохранению"
  },
  "en": {
    "title": "Regulatory strategy of the medicinal product",
    "close": "Close",
    "edit": "Edit",
    "cancel": "Cancel",
    "formNumberHint": "Forms number (Forms / Local SOPs)",
    "download": "Download",
    "save": "Save",
    "saving": "Saving…",
    "saveFailed": "Save failed",
    "saveNeedEdit": "Turn on editing first",
    "saveUnavailable": "Form is not ready to save yet"
  }
}
</i18n>

<style scoped>
.home-rsd-modal {
  /* Как LP: inset без vw/vh — корректно при CSS zoom на <html> */
  position: fixed !important;
  inset: 0 !important;
  width: auto !important;
  height: auto !important;
  max-width: none !important;
  max-height: none !important;
  margin: 0 !important;
  z-index: 1180;
  background: rgba(7, 27, 58, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0.75rem clamp(0.75rem, 1.5vw, 1.5rem);
  overflow: hidden;
  box-sizing: border-box;
}
.home-rsd-modal__card {
  width: 100%;
  max-width: 100%;
  flex: 1 1 0;
  align-self: stretch;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(0, 51, 160, 0.12);
  border-radius: 20px;
  box-shadow: 0 24px 80px rgba(0, 35, 90, 0.26);
}
.home-rsd-modal__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px 14px;
  border-bottom: 1px solid rgba(0, 51, 160, 0.08);
  background: #fff;
}
.home-rsd-modal__title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #071b3a;
  line-height: 1.25;
}
.home-rsd-modal__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 51, 160, 0.08);
  color: #0033a0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}
.home-rsd-modal__close:hover {
  background: rgba(229, 57, 53, 0.1);
  color: #e53935;
}
.home-rsd-modal__body {
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  background: #fff;
}
.home-rsd-modal__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 22px 18px;
  border-top: 1px solid rgba(0, 51, 160, 0.08);
  background: #fff;
}
.home-rsd-modal__foot-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
}
.home-rsd-modal__foot-left :deep(.history-link-wrapper) {
  margin: 0;
}
.home-rsd-modal__form-info {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #7a8e98;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 51, 160, 0.06);
  border: 1px solid rgba(0, 51, 160, 0.12);
}
.home-rsd-modal__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.home-rsd-modal__save-error {
  margin: 0;
  flex: 1 1 100%;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: #c62828;
}
.home-rsd-modal__btn {
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}
.home-rsd-modal__btn:hover:not(:disabled) {
  transform: translateY(-1px);
}
.home-rsd-modal__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.home-rsd-modal__btn--secondary {
  background: rgba(0, 51, 160, 0.07);
  color: #0033a0;
  border: 1px solid rgba(0, 51, 160, 0.14);
}
.home-rsd-modal__btn--secondary:hover:not(:disabled) {
  background: rgba(0, 51, 160, 0.12);
}
.home-rsd-modal__btn--primary {
  background: linear-gradient(135deg, #001f63, #0033a0);
  color: #fff;
  border: 1px solid rgba(0, 31, 99, 0.35);
  box-shadow: 0 8px 22px rgba(0, 51, 160, 0.28);
}
.home-rsd-modal__btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #0033a0, #0047c7);
  box-shadow: 0 10px 26px rgba(0, 51, 160, 0.34);
}
</style>
